import { DayOfWeek, LocalDate, TemporalAdjusters } from "@js-joda/core";
import { PeriodNavigator } from "components/designV2/periodNavigator/periodNavigator";
import { useMemo } from "react";
import type { RefObject } from "react";
import { useTranslation } from "react-i18next";
import {
  DayCard,
  DayDate,
  DayName,
  DaysRow,
  WeekHeader,
  WeekView,
} from "./scheduleWeekView.styled";
import {
  ScheduleRow,
  ScheduleCreatePayload,
  ShiftClickPayload,
} from "../../common/scheduleRow/scheduleRow.component";
import { ScheduleWorkerRow } from "web-apps/company/components/schedule/types/schedule.types";
import { ScheduleViewBody } from "../shared/scheduleViewBody.component";
import {
  formatDateKey,
  formatItemDateKey,
  formatDayShort,
  getStockholmLocalDateNow,
  formatWeekdayShort,
} from "web-apps/company/components/schedule/utils/schedule.dates";
import { GroupByOption } from "web-apps/company/components/schedule/components/hooks/useScheduleToolbarFilters";
import { GroupDivider } from "../shared/groupDivider.component";
import { getRowRenderKey } from "../shared/scheduleRow.key.logic";

interface Props {
  currentDate: LocalDate;
  onChangeDate: (nextDate: LocalDate) => void;
  shiftBlockSize: "small" | "large" | "pill";
  isLoading: boolean;
  isLoadingMore: boolean;
  isEmpty: boolean;
  emptyText: string;
  groupByOption: GroupByOption;
  workerRows: ScheduleWorkerRow[];
  onShiftClick: (payload: ShiftClickPayload) => void;
  onScheduleClick: (payload: ScheduleCreatePayload) => void;
  loadMoreRef: RefObject<HTMLDivElement>;
}

const getTodayLocalDate = (): LocalDate => getStockholmLocalDateNow();

const formatWeekdayLabel = (date: LocalDate, language: string): string =>
  formatWeekdayShort(date, language);

const formatDayLabel = (date: LocalDate): string => formatDayShort(date);

const WEEK_LEFT_COLUMN_WIDTH = 310;

export const ScheduleWeekView: React.FC<Props> = (props) => {
  const { i18n, t } = useTranslation();

  const weekStart = useMemo(
    () =>
      props.currentDate.with(
        TemporalAdjusters.previousOrSame(DayOfWeek.MONDAY),
      ),
    [props.currentDate],
  );

  const weekDays = useMemo(
    () => Array.from({ length: 7 }, (_, index) => weekStart.plusDays(index)),
    [weekStart],
  );
  const weekDayKeys = useMemo(
    () => new Set(weekDays.map((date) => formatDateKey(date))),
    [weekDays],
  );
  
  const visibleWorkerRows = useMemo(
    () =>
      props.workerRows.filter((workerRow) =>
        workerRow.sections.some((section) =>
          section.items.some((item) =>
            weekDayKeys.has(formatItemDateKey(item.startTime)),
          ),
        ),
      ),
    [props.workerRows, weekDayKeys],
  );
  const isViewEmpty = !props.isLoading && visibleWorkerRows.length === 0;
  const today = useMemo(() => getTodayLocalDate(), [props.currentDate]);
  const rowsWithDividers = useMemo(() => {
    if (props.groupByOption !== "team") {
      return visibleWorkerRows.map((workerRow) => ({
        key: getRowRenderKey(workerRow, props.groupByOption),
        row: workerRow,
        divider: null as JSX.Element | null,
      }));
    }

    const result: Array<{
      key: string;
      row: ScheduleWorkerRow;
      divider: JSX.Element | null;
    }> = [];
    let lastWorkGroupId: number | null = null;
    visibleWorkerRows.forEach((workerRow) => {
      const section = workerRow.sections[0];
      const workGroupId = section?.id ?? null;
      const rawUnitName = (section?.unitName ?? "").trim();
      const unitName =
        rawUnitName.length > 0 ? rawUnitName : t("Schedule.UnknownUnit");
      const rawTeamName = (section?.text ?? "").trim();
      const teamName =
        rawTeamName.length > 0 ? rawTeamName : t("Schedule.UnknownTeam");
      const shouldRenderDivider =
        workGroupId !== null && workGroupId !== lastWorkGroupId;
      result.push({
        key: getRowRenderKey(workerRow, props.groupByOption),
        row: workerRow,
        divider: shouldRenderDivider ? (
          <GroupDivider
            key={`divider-${workGroupId}`}
            unitName={unitName}
            teamName={teamName}
          />
        ) : null,
      });
      lastWorkGroupId = workGroupId;
    });
    return result;
  }, [props.groupByOption, t, visibleWorkerRows]);

  return (
    <WeekView>
      <WeekHeader>
        <PeriodNavigator
          currentDate={props.currentDate}
          mode="week"
          onChange={props.onChangeDate}
          showSubLabel
        />
        <DaysRow>
          {weekDays.map((date) => {
            const isToday = date.equals(today);
            return (
              <DayCard key={date.toString()} $isToday={isToday}>
                <DayName>{formatWeekdayLabel(date, i18n.language)}</DayName>
                <DayDate $isToday={isToday}>{formatDayLabel(date)}</DayDate>
              </DayCard>
            );
          })}
        </DaysRow>
      </WeekHeader>
      <ScheduleViewBody
        isLoading={props.isLoading}
        isEmpty={props.isEmpty || isViewEmpty}
        emptyText={props.emptyText}
        isLoadingMore={props.isLoadingMore}
        loadMoreRef={props.loadMoreRef}
        hasContent={visibleWorkerRows.length > 0}
        isDimmed={props.isLoading && visibleWorkerRows.length > 0}
      >
        {rowsWithDividers.map((entry) => (
          <div key={entry.key}>
            {entry.divider}
            <ScheduleRow
              workerRow={entry.row}
              days={weekDays}
              shiftBlockSize={props.shiftBlockSize}
              leftColumnWidth={WEEK_LEFT_COLUMN_WIDTH}
              onShiftClick={props.onShiftClick}
              onScheduleClick={props.onScheduleClick}
            />
          </div>
        ))}
      </ScheduleViewBody>
    </WeekView>
  );
};
